import { enumSchema } from '@shared/utils/enum.utils';

import { Company, Country, Holiday } from '../company';
import { ProjectStatus, Currency, TradeType, OwnershipType } from '../enums';
import { BuildingType } from '../enums/building-type.enum';
import { ProjectType } from '../enums/project-type.enum';
import { StaffingUnit, WorkingHours, Customer } from '../master-data';
import { TranslationDictionary } from '../translate';
import { User } from '../user';
import { DocumentFile } from '../utils';
import { currencySchema } from '../utils/money.model';

import { Activity } from './activity.model';
import { ConstructionArea } from './construction-area.model';
import { CostEstimateConfiguration } from './cost-estimate-configuration.model';
import { LabourCosts } from './labour-costs.model';
import { Phase } from './phase.model';
import { Plan } from './plan.model';
import { PriceFactor } from './price-factor.model';
import { Product } from './product.model';
import { ProjectSummary } from './project-summary.model';
import { QualityCheckItem } from './quality-check-item.model';
import { SubPhase } from './sub-phase.model';
import { WorkUnit, WorkUnitInTime, WorkUnitQaSummary } from './work-unit';

export interface Project {
  id: string;
  created?: Date;
  lastModified?: Date;

  masterProjectId?: string;
  masterProject?: LinkedProject;
  subProjects?: LinkedProject[];

  isRemoved?: boolean;

  lastCustomerContact?: Date;

  companyId: string;
  company?: Company;

  summary?: ProjectSummary;
  details: ProjectDetails;
  settings: ProjectSettings;

  costEstimateConfiguration: CostEstimateConfiguration;

  files: DocumentFile[];
  activities: Activity[];
  constructionsAreas: ConstructionArea[];
  phases: Phase[];
  subPhases?: SubPhase[];
  priceFactors: PriceFactor[];
  labourCosts: LabourCosts;
  products: Product[];
  staffingUnit?: StaffingUnit;

  defaultWorkingHours: WorkingHours;
  workingHours?: WorkingHours[];

  hoursInWeekForOneEmployeePlanned: HoursInWeekForOneEmployeePlanned[]; // how many work hour is in curent week for one worker
  workUnits: WorkUnit[];
  workUnitsInTime: WorkUnitInTime[];
  milestones: ProjectMilestone[];
  holidays?: Holiday[];
  qualityCheckItems: QualityCheckItem[];
  plans: Plan[];
}

export interface ProjectMilestone {
  id?: string;
  name?: TranslationDictionary;
  description?: TranslationDictionary;
  color?: string;
  date: string;
  imageId?: string;
  image?: DocumentFile;
}

export interface ProjectDetails {
  id?: string;
  name: string;
  projectNumber: string;
  color?: string;
  status: ProjectStatus;
  startDate: Date;
  endDate: Date;
  notes: string;
  currency: Currency;
  isMetric: boolean;
  tenderDeadline: Date;
  tradeTypes: TradeType[];
  type: ProjectType;
  buildingType: BuildingType;
  ownershipType: OwnershipType;

  menuNotes: string;

  customerId: string;
  customer?: Customer;

  contactName: string;
  contactEmail: string;
  contactPhone: string;

  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  state: string;
  countryId: string;
  country?: Country;
  formattedAddress?: string;
  image?: DocumentFile;
  imageId: string;

  responsibleProjectManagerUserId: string;
  responsibleProjectManagerUser?: User;
  assistantProjectManagersUserIds: string[];
}

export interface LinkedProjectDetails {
  name: string;
  projectNumber: string;
  startDate?: Date;
  endDate?: Date;
  tenderDeadline?: Date;
  responsibleProjectManagerUserId: string;
  responsibleProjectManagerUser?: User;
}

export interface LinkedProject {
  id: string;

  companyId: string;
  companyName: string;

  name: string;
  status: ProjectStatus;
  color?: string;

  details?: LinkedProjectDetails;

  activities: Activity[];
  constructionsAreas: ConstructionArea[];
  phases: Phase[];
  subPhases?: SubPhase[];
  products?: Product[];
  summary?: LinkedProjectSummary;
  workUnits?: LinkedProjectWorkUnit[];
  workUnitsInTime?: LinkedProjectWorkUnitInTime[];
  milestones?: ProjectMilestone[];
}

export type LinkedProjectSummary = Pick<
  ProjectSummary,
  | 'workUnitsPerWeek'
  | 'installationTime'
  | 'totalSalesPrice'
  | 'plannedProgress'
  | 'actualProgress'
  | 'totalQualityChecks'
  | 'doneQualityChecks'
  | 'deviations'
>;

export type LinkedProjectWorkUnit = Pick<
  WorkUnit,
  'id' | 'projectId' | 'constructionAreaId' | 'activityId' | 'phaseId' | 'subPhaseId'
> & {
  qaSummary?: WorkUnitQaSummary;
};

export type LinkedProjectWorkUnitInTime = Pick<
  WorkUnitInTime,
  'id' | 'workUnitId' | 'projectId' | 'day' | 'employeesCountWorkingPerWeekPlanned'
>;

export interface ProjectSettings {
  constructionAreasAlias: string;
  activitiesAlias: string;
  phasesAlias: string;
  hasSubPhases?: boolean;
  productImportOptionsJson?: string;
}

export interface HoursInWeekForOneEmployeePlanned {
  id: string;
  day: string;
  value: number;
}

export const projectDetailsChatbotFields = {
  name: { type: 'string' },
  projectNumber: { type: 'string' },
  startDate: { type: 'string', description: 'Project start date in ISO 8601 format.' },
  endDate: { type: 'string', description: 'Project end date in ISO 8601 format.' },
  notes: { type: 'string' },
  currency: currencySchema,
  isMetric: { type: 'boolean' },
  tenderDeadline: { type: 'string', description: 'Tender deadline date in ISO 8601 format.' },
  tradeTypes: { type: 'array', items: enumSchema('TradeType', TradeType) },
  type: enumSchema('ProjectType', ProjectType),
  buildingType: enumSchema('BuildingType', BuildingType),
  ownershipType: enumSchema('OwnershipType', OwnershipType),
  menuNotes: { type: 'string' },
  contactName: { type: 'string' },
  contactEmail: { type: 'string' },
  contactPhone: { type: 'string' },
  addressLine1: { type: 'string' },
  addressLine2: { type: 'string' },
  city: { type: 'string' },
  postalCode: { type: 'string' },
  state: { type: 'string' },
  countryId: { type: 'string' },
};
